import { useCallback, useMemo, useState } from 'react';

import { PALFilterBody } from './pal-filter-body';
import { PALFilterMenuItem } from './pal-filter-menu-item';
import {
  SegmentedButton,
  HouseUserIcon,
  UserIcon,
  LocationDotIcon,
  FilterComboBoxProps,
  Spinner,
  FilterComboBoxWrapper,
  useFilterComboboxSelection,
} from '../..';
import { matchesSearch } from '../filter-combo-box/filter-combo-box-menu-item';
import { HighlightedMenuItem } from '../filter-combo-box/filter-combo-box.types';

const classes = {
  wrapper: 'inline-block relative',
  input: `bg-white rounded-t-lg py-2 px-3 border-none shadow-dark`,
  popover: {
    root: 'origin-top-right absolute z-150',
    base: 'flex-col max-h-104 overflow-y-auto rounded-t-none border-t border-t-gray-300 shadow-dark',
    clearSelectionButton:
      'text-primary ml-auto hover:text-jungle-400 active:underline cursor-pointer select-none font-medium decoration-primary text-xs hover:underline',
    headerWrapper: 'px-4 py-3 flex-row flex',
    headerText: 'font-medium text-gray-600 uppercase text-xs',
    sectionBorder: 'border-gray-300',
  },
};

export type PALResultType = 'All' | 'Portfolio' | 'Account' | 'Location';

export interface PALFilterProps<T extends HighlightedMenuItem> extends Omit<FilterComboBoxProps<T>, 'data'> {
  /** The list of portfolio menu items */
  portfolios: T[];
  /** The list of account menu items */
  accounts: T[];
  /** The list of location menu items */
  locations: T[];
  /** Which types of entities should be returned from the server and displayed in the dropdown. */
  resultType: PALResultType;
  onResultTypeChange: (filterType: PALResultType) => void;
  /** Whether or not to display the loading state */
  isLoading: boolean;
}

// eslint-disable-next-line max-lines-per-function
export const PALFilter = <T extends HighlightedMenuItem>(props: PALFilterProps<T>) => {
  const {
    portfolios,
    accounts,
    locations,
    onChange,
    onResultTypeChange,
    autoFilterItems = false,
    onSearch: onSearchCallback,
    renderListItem,
    defaultValue = [],
    defaultSearchTerm,
    resultType,
    isLoading,
    ...rest
  } = props;
  const [searchTerm, setSearchTerm] = useState<string>(defaultSearchTerm ?? '');
  const data = useMemo(() => [...portfolios, ...accounts, ...locations], [portfolios, accounts, locations]);
  const { selectedItems, onToggleItem, deselectAll } = useFilterComboboxSelection(defaultValue, onChange, data);

  const isMatch = useCallback(
    (item: T) => !autoFilterItems || matchesSearch(item, searchTerm),
    [autoFilterItems, searchTerm]
  );

  const onSearch = (searchString: string) => {
    setSearchTerm(searchString);
    onSearchCallback(searchString);
  };

  const menuItemRenderer = useCallback(
    (value: T, selected = false) => (
      <PALFilterMenuItem
        key={value.value}
        item={{ ...value, textToHighlight: searchTerm }}
        defaultSelection={selected}
        onSelect={onToggleItem}
        className="text-sm"
        renderItem={renderListItem}
      />
    ),
    [onToggleItem, renderListItem, searchTerm]
  );

  const getNotSelected = useCallback(
    (data: T[]) => {
      return data
        .filter(
          (dataItem) =>
            !selectedItems.some((selectedItem) => selectedItem.value === dataItem.value) && isMatch(dataItem)
        )
        .map((element) => menuItemRenderer(element, false));
    },
    [isMatch, menuItemRenderer, selectedItems]
  );

  const portfoliosNotSelected = useMemo(() => getNotSelected(portfolios), [portfolios, getNotSelected]);
  const accountsNotSelected = useMemo(() => getNotSelected(accounts), [accounts, getNotSelected]);
  const locationsNotSelected = useMemo(() => getNotSelected(locations), [locations, getNotSelected]);
  const itemsSelected = useMemo(
    () =>
      selectedItems
        .filter((selectedItem) => matchesSearch(selectedItem, searchTerm))
        .map((element) => menuItemRenderer(element, true)),
    [menuItemRenderer, searchTerm, selectedItems]
  );

  return (
    <FilterComboBoxWrapper
      popoverClassesBase={classes.popover.base}
      isDropdownEmpty={false}
      onSearch={onSearch}
      {...rest}
    >
      {selectedItems.length > 0 && (
        <div className={classes.popover.headerWrapper}>
          <div className={classes.popover.headerText}>Selected ({selectedItems.length})</div>
          <div
            role="button"
            aria-label="Clear selection"
            onClick={deselectAll}
            className={classes.popover.clearSelectionButton}
          >
            Clear selected
          </div>
        </div>
      )}
      {itemsSelected}
      <div className={classes.popover.headerWrapper}>
        <div className={classes.popover.headerText}>Results</div>
      </div>
      <div className="mx-4 mb-3 text-sm leading-6">
        <SegmentedButton
          type="radio"
          name="seg-button-1"
          data={[
            { label: 'All', value: 'All' },
            { label: 'Portfolio', value: 'Portfolio', icon: HouseUserIcon },
            { label: 'Account', value: 'Account', icon: UserIcon },
            { label: 'Location', value: 'Location', icon: LocationDotIcon },
          ]}
          size="medium"
          buttonClassName="flex-1"
          iconClassName="text-gray-800 fill-current"
          value={[resultType]}
          onChange={(selected: string | string[]) => onResultTypeChange(selected as PALResultType)}
        />
      </div>
      {isLoading ? (
        <div className="flex h-80 items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <PALFilterBody
          portfoliosNotSelected={portfoliosNotSelected}
          accountsNotSelected={accountsNotSelected}
          locationsNotSelected={locationsNotSelected}
        />
      )}
    </FilterComboBoxWrapper>
  );
};

export default PALFilter;
